import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Camera,
  Check,
  Copy,
  Eye,
  Layers,
  MapPin,
  Palette,
  QrCode,
  RefreshCw,
  ScanLine,
  Search,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import {
  buildDeckRecognitionPool,
  computeDeterministicCardArtHash,
  computeGrayscaleDHash64,
  recognizeCardFromDeckPool,
  recognizeMultiCardRowFromPool,
  type CardDefinition,
  type ClientGameView,
  type GameIntent,
  type PaperCameraMode,
  type PaperVideoMarker,
  type PublicCardView,
} from '#shared';
import { playtableApi } from '../lib/clientApi.ts';

export type PaperModalTab = 'camera' | 'scanner' | 'tools';

export function PaperWebcamModal({
  view,
  initialTab = 'camera',
  onDispatch,
  onSendWebRtcSignal,
  onInspectCardName,
  onClose,
}: {
  view: ClientGameView;
  initialTab?: PaperModalTab;
  onDispatch: (intent: GameIntent) => void;
  onSendWebRtcSignal?: (signalKind: string, data: unknown, targetSeatId?: string) => void;
  onInspectCardName?: (cardName: string) => void;
  onClose: () => void;
}) {
  const mySeat = view.viewerSeatId ? view.seats[view.viewerSeatId] : null;
  const paper = mySeat?.paperMode;

  const [tab, setTab] = useState<PaperModalTab>(initialTab);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // QR Overhead phone camera pairing
  const [phonePair, setPhonePair] = useState<{
    code: string;
    pairUrl: string;
    qrSvg: string;
  } | null>(null);
  const [copiedPair, setCopiedPair] = useState(false);

  // Deck-scoped scanner state
  const [ocrHint, setOcrHint] = useState('');
  const [capturedDHash, setCapturedDHash] = useState<string>('');
  const [scanDestZone, setScanDestZone] = useState<'battlefield' | 'graveyard' | 'exile' | 'command'>('battlefield');
  const [tokenCopyCount, setTokenCopyCount] = useState<number>(1);
  const [multiRowInput, setMultiRowInput] = useState('');
  const [multiRowHits, setMultiRowHits] = useState<string[]>([]);

  // Off-list paper card search
  const [offListQuery, setOffListQuery] = useState('');
  const [offListHits, setOffListHits] = useState<CardDefinition[]>([]);
  const [offListScope, setOffListScope] = useState<'this_game_only' | 'save_deck_update'>('this_game_only');

  // Video marker quick form
  const [markerLabel, setMarkerLabel] = useState('+1/+1');
  const [markerKind, setMarkerKind] = useState<PaperVideoMarker['kind']>('counter');
  const [markerX, setMarkerX] = useState(50);
  const [markerY, setMarkerY] = useState(50);
  const [markerCount, setMarkerCount] = useState(1);

  // Custom alter / proxy registration
  const [alterCardName, setAlterCardName] = useState('');
  const [alterArtistCredit, setAlterArtistCredit] = useState('');
  const [alterDHashHex, setAlterDHashHex] = useState('');
  const [statusNotice, setStatusNotice] = useState<string | null>(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
      if (streamRef.current) {
        for (const t of streamRef.current.getTracks()) t.stop();
        streamRef.current = null;
      }
    };
  }, [onClose]);

  // Build deck-scoped recognition pool from seat's known deck art/cards + tokens + alters
  const deckPool = useMemo(() => {
    if (!mySeat) return [];
    const uniqueDeckCards: CardDefinition[] = [];
    const seen = new Set<string>();

    const pushCard = (c: CardDefinition | null | undefined) => {
      if (!c) return;
      const k = c.name.toLowerCase();
      if (seen.has(k)) return;
      seen.add(k);
      uniqueDeckCards.push(c);
    };

    for (const pv of [
      ...(mySeat.zones.handCards ?? []),
      ...mySeat.zones.battlefield,
      ...mySeat.zones.command,
      ...mySeat.zones.graveyard,
      ...mySeat.zones.exile,
      ...(mySeat.zones.sideboardCards ?? []),
    ]) {
      pushCard(pv.card);
    }

    for (const art of mySeat.deckMeta?.legalCosmeticArt ?? []) {
      const k = art.cardName.toLowerCase();
      if (!seen.has(k)) {
        seen.add(k);
        uniqueDeckCards.push({
          scryfallId: art.scryfallId,
          oracleId: `oracle_${art.scryfallId}`,
          name: art.cardName,
          manaCost: '',
          cmc: 0,
          typeLine: 'Card',
          oracleText: '',
          colors: art.colors,
          colorIdentity: art.colors,
          imageUrl: art.artCropUrl,
          artCropUrl: art.artCropUrl,
          isGameChanger: false,
        });
      }
    }

    return buildDeckRecognitionPool({
      deckCards: uniqueDeckCards,
      tokens: mySeat.availableTokens,
      registeredAlters: mySeat.paperMode.registeredAlters,
    });
  }, [mySeat]);

  const recognitionResult = useMemo(() => {
    return recognizeCardFromDeckPool(deckPool, {
      frameDHashHex: capturedDHash || null,
      ocrTitleHint: ocrHint || null,
    });
  }, [deckPool, capturedDHash, ocrHint]);

  const startLocalCameraPreview = useCallback(async () => {
    setCameraError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError(
        'Browser camera API unavailable (HTTPS or localhost required). You can still pair a phone overhead camera via QR or use Deck-Limited Title Scanner.'
      );
      return;
    }
    try {
      if (streamRef.current) {
        for (const t of streamRef.current.getTracks()) t.stop();
      }
      const ratio = paper?.aspectRatio === '4:3' ? 4 / 3 : 16 / 9;
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { aspectRatio: ratio, width: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = stream;
      setCameraActive(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      onSendWebRtcSignal?.('camera-status', { active: true, mode: paper?.cameraMode ?? 'device' });
    } catch (err: any) {
      setCameraActive(false);
      setCameraError(
        `Camera permission denied or device busy (${err?.name ?? 'Error'}). Allow camera access in your browser address bar and click Retry Camera.`
      );
    }
  }, [paper?.aspectRatio, paper?.cameraMode, onSendWebRtcSignal]);

  const captureVideoDHash = useCallback((): string | null => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) return null;
    const canvas = document.createElement('canvas');
    canvas.width = 9;
    canvas.height = 8;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, 9, 8);
    const rgba = ctx.getImageData(0, 0, 9, 8).data;
    const lum = new Uint8Array(72);
    for (let i = 0; i < 72; i++) {
      const r = rgba[i * 4];
      const g = rgba[i * 4 + 1];
      const b = rgba[i * 4 + 2];
      lum[i] = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
    }
    return computeGrayscaleDHash64(lum);
  }, []);

  const broadcastLowBandwidthStill = useCallback(() => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) {
      setStatusNotice('Start local camera preview first to broadcast a still playmat snapshot.');
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = 480;
    canvas.height = 270;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, 480, 270);
    const jpegDataUrl = canvas.toDataURL('image/jpeg', 0.55);
    onSendWebRtcSignal?.('still-frame', { jpegDataUrl, capturedAt: Date.now() });
    setStatusNotice('Sent low-bandwidth Table-Only still frame to peers (zero server storage).');
  }, [onSendWebRtcSignal]);

  useEffect(() => {
    if (!offListQuery.trim()) {
      setOffListHits([]);
      return;
    }
    const t = setTimeout(() => {
      playtableApi
        .searchCards(offListQuery.trim(), false)
        .then((res) => setOffListHits(res.cards.slice(0, 6)))
        .catch(() => {});
    }, 180);
    return () => clearTimeout(t);
  }, [offListQuery]);

  if (!mySeat || !paper) return null;

  const qrSvgUri = phonePair ? `data:image/svg+xml;utf8,${encodeURIComponent(phonePair.qrSvg)}` : null;

  return (
    <div
      className="fixed inset-0 z-[105] bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-3"
      role="dialog"
      aria-modal="true"
      aria-label="Paper and Webcam Hybrid Mode"
    >
      <div className="w-full max-w-4xl max-h-[90vh] rounded-2xl bg-[#0d1322] border border-slate-700/90 shadow-2xl flex flex-col overflow-hidden text-xs">
        {/* Header */}
        <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between bg-slate-950/80">
          <div className="flex items-center gap-2.5">
            <Camera className="w-4 h-4 text-emerald-400" />
            <div>
              <h2 className="font-extrabold text-sm text-white">
                Paper & Webcam Hybrid Mode (Deck-Limited Recognition)
              </h2>
              <p className="text-[11px] text-slate-400">
                Play physical paper decks alongside digital players · Zero server video recording · Instant on-device deck pool recognition ({deckPool.length} cards/tokens indexed)
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close Paper Webcam Modal"
            className="p-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-slate-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Sub-Tabs */}
        <div className="px-4 pt-2.5 flex flex-wrap items-center justify-between gap-2 border-b border-slate-800/80 pb-2.5">
          <div className="flex items-center gap-1.5">
            {(
              [
                ['camera', '1. Camera, Table-Only Crop & QR Phone'],
                ['scanner', '2. Deck Scanner, Digital Twin & Seen Log'],
                ['tools', '3. Off-List Paper Update, Markers & Alters'],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                className={`px-3 py-1.5 rounded-xl font-bold ${
                  tab === id
                    ? 'bg-emerald-500 text-slate-950'
                    : 'bg-slate-900 hover:bg-slate-800 text-slate-300'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() =>
              onDispatch({
                type: 'CONFIGURE_PAPER_MODE',
                enabled: !paper.enabled,
                consentAccepted: paper.consentAccepted,
                cameraMode: !paper.enabled ? paper.cameraMode : 'off',
              })
            }
            className={`px-3 py-1.5 rounded-xl font-extrabold ${
              paper.enabled
                ? 'bg-emerald-500/20 border border-emerald-400 text-emerald-200'
                : 'bg-slate-900 border border-slate-700 text-slate-300'
            }`}
          >
            {paper.enabled ? 'Paper Hybrid Seat: ON' : 'Enable Paper Seat'}
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {statusNotice && (
            <div className="px-3 py-2 rounded-xl bg-emerald-950/70 border border-emerald-500/50 text-emerald-200 flex items-center justify-between">
              <span>{statusNotice}</span>
              <button type="button" onClick={() => setStatusNotice(null)} className="text-slate-400 hover:text-white">
                ✕
              </button>
            </div>
          )}

          {/* TAB 1: CAMERA, PRIVACY CROP & PHONE QR */}
          {tab === 'camera' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="p-3 rounded-xl bg-slate-900 border border-slate-800 space-y-2.5">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={paper.consentAccepted}
                      onChange={(e) =>
                        onDispatch({
                          type: 'CONFIGURE_PAPER_MODE',
                          enabled: e.target.checked ? true : paper.enabled,
                          consentAccepted: e.target.checked,
                          cameraMode: e.target.checked ? paper.cameraMode : 'off',
                        })
                      }
                      className="mt-0.5 accent-emerald-500"
                    />
                    <span className="text-[11px] text-slate-200 leading-snug">
                      <strong>Explicit Camera Privacy Consent:</strong> Share my cropped Table-Only playmat feed peer-to-peer with this pod. Playtable never records or stores video on the server.
                    </span>
                  </label>

                  <div className="grid grid-cols-2 gap-1.5">
                    {(
                      [
                        ['device', 'Device Webcam'],
                        ['phone_qr', 'Phone Overhead (QR)'],
                        ['low_bandwidth_still', 'Low-Bandwidth Stills'],
                        ['off', 'Camera Off (Digital Twin)'],
                      ] as [PaperCameraMode, string][]
                    ).map(([mode, label]) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() =>
                          onDispatch({
                            type: 'CONFIGURE_PAPER_MODE',
                            enabled: true,
                            cameraMode: mode,
                          })
                        }
                        className={`py-1.5 px-2 rounded-lg font-bold border text-[11px] ${
                          paper.cameraMode === mode
                            ? 'bg-emerald-500 text-slate-950 border-emerald-400'
                            : 'bg-slate-950 border-slate-800 text-slate-300'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <label className="space-y-1">
                      <span className="text-[10px] font-bold text-slate-400 uppercase">Codec</span>
                      <select
                        value={paper.preferredCodec}
                        onChange={(e) =>
                          onDispatch({
                            type: 'CONFIGURE_PAPER_MODE',
                            preferredCodec: e.target.value as 'vp9' | 'vp8' | 'h264',
                          })
                        }
                        className="w-full px-2 py-1.5 rounded-lg bg-slate-950 border border-slate-700 text-white"
                      >
                        <option value="vp9">VP9 (Sharpest text)</option>
                        <option value="vp8">VP8 (Broadest WebRTC)</option>
                        <option value="h264">H.264 (Hardware battery saver)</option>
                      </select>
                    </label>
                    <label className="space-y-1">
                      <span className="text-[10px] font-bold text-slate-400 uppercase">Aspect Ratio</span>
                      <select
                        value={paper.aspectRatio}
                        onChange={(e) =>
                          onDispatch({
                            type: 'CONFIGURE_PAPER_MODE',
                            aspectRatio: e.target.value as '16:9' | '4:3',
                          })
                        }
                        className="w-full px-2 py-1.5 rounded-lg bg-slate-950 border border-slate-700 text-white"
                      >
                        <option value="16:9">16:9 Playmat Widescreen</option>
                        <option value="4:3">4:3 Overhead Rig</option>
                      </select>
                    </label>
                  </div>
                </div>

                {/* Table-Only Mode Crop & Privacy Mask */}
                <div className="p-3 rounded-xl bg-slate-900 border border-slate-800 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-amber-300">Table-Only Framing Crop (0–40%)</span>
                    <label className="inline-flex items-center gap-1.5 text-[11px] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={paper.privacyBlur}
                        onChange={(e) =>
                          onDispatch({
                            type: 'CONFIGURE_PAPER_MODE',
                            privacyBlur: e.target.checked,
                          })
                        }
                        className="accent-amber-500"
                      />
                      <span>Edge Privacy Blur</span>
                    </label>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {(['top', 'bottom', 'left', 'right'] as const).map((edge) => (
                      <label key={edge} className="space-y-0.5">
                        <div className="flex justify-between text-[10px] text-slate-400 capitalize">
                          <span>{edge} crop</span>
                          <span>{paper.tableOnlyCrop[edge]}%</span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={40}
                          value={paper.tableOnlyCrop[edge]}
                          onChange={(e) =>
                            onDispatch({
                              type: 'CONFIGURE_PAPER_MODE',
                              tableOnlyCrop: {
                                ...paper.tableOnlyCrop,
                                [edge]: Number(e.target.value),
                              },
                            })
                          }
                          className="w-full accent-amber-500"
                        />
                      </label>
                    ))}
                  </div>
                </div>

                {/* Pair Phone Overhead Camera via QR */}
                <div className="p-3 rounded-xl bg-slate-900 border border-slate-800 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-indigo-300 flex items-center gap-1">
                      <QrCode className="w-3.5 h-3.5" />
                      <span>Pair Phone as Overhead Playmat Camera</span>
                    </span>
                    <button
                      type="button"
                      onClick={async () => {
                        const res = await playtableApi.createCompanionPairCode(
                          view.tableId,
                          'overhead_camera'
                        );
                        setPhonePair(res);
                      }}
                      className="px-2.5 py-1 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-[10px]"
                    >
                      Generate QR
                    </button>
                  </div>
                  {phonePair && qrSvgUri && (
                    <div className="p-2 rounded-xl bg-slate-950 border border-indigo-500/40 flex items-center gap-3">
                      <img
                        src={qrSvgUri}
                        alt={`Overhead camera code ${phonePair.code}`}
                        className="w-24 h-24 rounded bg-white p-1 shrink-0"
                      />
                      <div className="space-y-1">
                        <div className="text-[10px] text-slate-400">Scan on phone browser (no login needed):</div>
                        <div className="font-mono font-black text-sm tracking-widest text-amber-300">
                          {phonePair.code}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard?.writeText(phonePair.pairUrl);
                            setCopiedPair(true);
                            setTimeout(() => setCopiedPair(false), 1500);
                          }}
                          className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-[10px] inline-flex items-center gap-1"
                        >
                          {copiedPair ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                          <span>{copiedPair ? 'Copied!' : 'Copy Phone Camera Link'}</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right: Live Camera Preview + Error Retry + Still Snapshot */}
              <div className="p-3 rounded-xl bg-slate-900 border border-slate-800 flex flex-col justify-between gap-3">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-slate-200">Table-Only Live Preview</span>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={startLocalCameraPreview}
                      className="px-2.5 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-[10px] inline-flex items-center gap-1"
                    >
                      <RefreshCw className="w-3 h-3" />
                      <span>{cameraActive ? 'Restart Camera' : 'Start / Retry Camera'}</span>
                    </button>
                    {paper.cameraMode === 'low_bandwidth_still' && (
                      <button
                        type="button"
                        onClick={broadcastLowBandwidthStill}
                        className="px-2.5 py-1 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-[10px]"
                      >
                        Send Still Snapshot
                      </button>
                    )}
                  </div>
                </div>

                {cameraError && (
                  <div className="p-2.5 rounded-xl bg-rose-950/80 border border-rose-500/60 text-rose-200 text-[11px] flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                    <span>{cameraError}</span>
                  </div>
                )}

                <div className="relative rounded-xl bg-slate-950 border border-slate-800 overflow-hidden aspect-video flex items-center justify-center">
                  <video
                    ref={videoRef}
                    muted
                    playsInline
                    className={`w-full h-full object-cover ${paper.privacyBlur ? ' contrast-105' : ''}`}
                    style={{
                      clipPath: `inset(${paper.tableOnlyCrop.top}% ${paper.tableOnlyCrop.right}% ${paper.tableOnlyCrop.bottom}% ${paper.tableOnlyCrop.left}%)`,
                    }}
                  />
                  {!cameraActive && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500 gap-1 p-4 text-center">
                      <Camera className="w-7 h-7 text-slate-600" />
                      <span>
                        Accept camera consent and click &ldquo;Start / Retry Camera&rdquo; to preview your cropped physical playmat.
                      </span>
                    </div>
                  )}
                </div>

                <p className="text-[10px] text-slate-400 leading-relaxed">
                  <strong>Tip:</strong> Even with your camera off or on low-bandwidth Wi-Fi, confirming cards in Tab 2 populates your crisp <strong>Digital Twin Board</strong> for mobile players and OBS viewers!
                </p>
              </div>
            </div>
          )}

          {/* TAB 2: DECK-SCOPED CARD SCANNER, MULTI-CARD ROW & SEEN LOG */}
          {tab === 'scanner' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="p-3 rounded-xl bg-slate-900 border border-slate-800 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-emerald-300 flex items-center gap-1.5">
                      <ScanLine className="w-3.5 h-3.5" />
                      <span>Deck-Scoped Perceptual + Title Scanner</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        const hash = captureVideoDHash();
                        if (hash) {
                          setCapturedDHash(hash);
                          setStatusNotice(`Captured 64-bit dHash (${hash}) from camera frame.`);
                        } else {
                          setStatusNotice('No active video frame — type card title prefix below for instant deck-scoped match!');
                        }
                      }}
                      className="px-2.5 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-[10px]"
                    >
                      Snap Camera Frame
                    </button>
                  </div>

                  <input
                    value={ocrHint}
                    onChange={(e) => setOcrHint(e.target.value)}
                    placeholder="Card title OCR / quick-filter within your loaded deck..."
                    className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white"
                  />

                  <div className="flex items-center justify-between gap-2 text-[11px]">
                    <label className="flex items-center gap-1.5">
                      <span className="text-slate-400">Target Zone:</span>
                      <select
                        value={scanDestZone}
                        onChange={(e) => setScanDestZone(e.target.value as any)}
                        className="px-2 py-1 rounded bg-slate-950 border border-slate-700 text-white"
                      >
                        <option value="battlefield">Digital Twin Battlefield</option>
                        <option value="command">Command Zone</option>
                        <option value="graveyard">Graveyard</option>
                        <option value="exile">Exile</option>
                      </select>
                    </label>
                    <label className="flex items-center gap-1.5">
                      <span className="text-slate-400">Token ×N:</span>
                      <input
                        type="number"
                        min={1}
                        max={20}
                        value={tokenCopyCount}
                        onChange={(e) => setTokenCopyCount(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
                        className="w-12 px-1.5 py-1 rounded bg-slate-950 border border-slate-700 text-center text-white"
                      />
                    </label>
                  </div>

                  {/* Top Matches within Deck */}
                  {recognitionResult.candidates.length > 0 ? (
                    <div className="space-y-1.5 pt-1">
                      {recognitionResult.candidates.map((cand) => (
                        <div
                          key={`${cand.isToken ? 'tok' : 'c'}_${cand.cardName}`}
                          className="p-2 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-between gap-2"
                        >
                          <div className="min-w-0">
                            <div className="font-bold text-white truncate flex items-center gap-1.5">
                              <span>{cand.cardName}</span>
                              <span className="px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-300 text-[9px]">
                                {Math.round(cand.confidence * 100)}%
                              </span>
                              {cand.alterArtistCredit && (
                                <span className="px-1.5 py-0.2 rounded bg-violet-500/20 text-violet-200 text-[9px]">
                                  Alter: {cand.alterArtistCredit}
                                </span>
                              )}
                            </div>
                            <div className="text-[10px] text-slate-400 truncate">{cand.typeLine}</div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => {
                                if (!paper.enabled) {
                                  onDispatch({ type: 'CONFIGURE_PAPER_MODE', enabled: true });
                                }
                                onDispatch({
                                  type: 'CONFIRM_PAPER_SCAN',
                                  cardName: cand.cardName,
                                  toZone: scanDestZone,
                                });
                              }}
                              className="px-2.5 py-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold text-[10px]"
                            >
                              1-Tap Confirm
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (!paper.enabled) {
                                  onDispatch({ type: 'CONFIGURE_PAPER_MODE', enabled: true });
                                }
                                onDispatch({
                                  type: 'CONFIRM_PAPER_SCAN',
                                  cardName: cand.cardName,
                                  asTokenCopy: true,
                                  count: tokenCopyCount,
                                });
                              }}
                              title="Spawn Digital Token Copy on Digital Twin Board"
                              className="px-2 py-1 rounded-lg bg-amber-500/20 hover:bg-amber-500/35 border border-amber-400/50 text-amber-200 font-bold text-[10px]"
                            >
                              ×{tokenCopyCount} Token
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-[11px] text-slate-500 py-2">
                      Snap a frame or type 2+ letters of a card in your deck to match instantly.
                    </div>
                  )}

                  {recognitionResult.isOffListWarning && ocrHint.trim().length >= 3 && (
                    <div className="p-2 rounded-lg bg-amber-950/60 border border-amber-500/50 text-amber-200 text-[10px] flex items-center justify-between gap-2">
                      <span>Card appears outside your loaded deck list.</span>
                      <button
                        type="button"
                        onClick={() => {
                          setOffListQuery(ocrHint.trim());
                          setTab('tools');
                        }}
                        className="px-2 py-0.5 rounded bg-amber-500 text-slate-950 font-bold shrink-0"
                      >
                        Resolve Off-List →
                      </button>
                    </div>
                  )}
                </div>

                {/* Multi-Card Still Row Scanner */}
                <div className="p-3 rounded-xl bg-slate-900 border border-slate-800 space-y-2">
                  <div className="font-bold text-cyan-300">Multi-Card Row Scan (Tutor / Reveal Pile)</div>
                  <input
                    value={multiRowInput}
                    onChange={(e) => setMultiRowInput(e.target.value)}
                    placeholder="Comma-separated row titles (e.g. Sol Ring, Arcane Signet, Command Tower)"
                    className="w-full px-2.5 py-1.5 rounded-lg bg-slate-950 border border-slate-700 text-white"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        const parts = multiRowInput
                          .split(',')
                          .map((s) => s.trim())
                          .filter(Boolean);
                        const batch = recognizeMultiCardRowFromPool(
                          deckPool,
                          parts.map((ocrTitleHint) => ({ ocrTitleHint }))
                        );
                        setMultiRowHits(
                          batch
                            .map((r) => r.topMatch?.cardName)
                            .filter((n): n is string => Boolean(n))
                        );
                      }}
                      className="px-2.5 py-1 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white font-bold text-[10px]"
                    >
                      Scan Row ({multiRowInput ? multiRowInput.split(',').filter((x) => x.trim()).length : 0})
                    </button>
                    {multiRowHits.length > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          if (!paper.enabled) {
                            onDispatch({ type: 'CONFIGURE_PAPER_MODE', enabled: true });
                          }
                          for (const name of multiRowHits) {
                            onDispatch({
                              type: 'CONFIRM_PAPER_SCAN',
                              cardName: name,
                              toZone: scanDestZone,
                            });
                          }
                          setMultiRowHits([]);
                        }}
                        className="px-2.5 py-1 rounded-lg bg-emerald-500 text-slate-950 font-extrabold text-[10px]"
                      >
                        Confirm All {multiRowHits.length} Matched Cards
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Right: "Cards Seen" Log & Oracle Reader */}
              <div className="p-3 rounded-xl bg-slate-900 border border-slate-800 flex flex-col justify-between space-y-2">
                <div>
                  <div className="font-bold text-slate-200 flex items-center justify-between mb-2">
                    <span className="flex items-center gap-1.5">
                      <Layers className="w-3.5 h-3.5 text-amber-400" />
                      <span>Cards Seen Log ({paper.scannedCardsSeen.length})</span>
                    </span>
                    <span className="text-[10px] text-slate-400">Click any card for Oracle text</span>
                  </div>

                  {paper.scannedCardsSeen.length === 0 ? (
                    <p className="text-slate-500 text-[11px] py-6 text-center">
                      Confirmed paper scans appear here and on your Digital Twin Board so mobile & OBS viewers can inspect crisp Oracle text regardless of camera glare or alter art.
                    </p>
                  ) : (
                    <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                      {paper.scannedCardsSeen.map((entry) => (
                        <button
                          key={entry.scanId}
                          type="button"
                          onClick={() => onInspectCardName?.(entry.cardName)}
                          className="w-full p-2 rounded-lg bg-slate-950 hover:bg-slate-800 border border-slate-800 flex items-center justify-between text-left"
                        >
                          <div>
                            <div className="font-bold text-white">{entry.cardName}</div>
                            {entry.artistCredit && (
                              <div className="text-[10px] text-violet-300">
                                Alter art credit: {entry.artistCredit}
                              </div>
                            )}
                          </div>
                          <span className="px-2 py-0.5 rounded bg-slate-900 text-[10px] uppercase text-slate-400">
                            {entry.zone}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: OFF-LIST PAPER RESOLVER, PINNED VIDEO MARKERS & ALTER REGISTRATION */}
          {tab === 'tools' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* 1. Off-List Paper Card Live Update */}
              <div className="p-3 rounded-xl bg-slate-900 border border-slate-800 space-y-2">
                <div className="font-bold text-amber-300 flex items-center gap-1">
                  <Search className="w-3.5 h-3.5" />
                  <span>Off-List Paper Card Update</span>
                </div>
                <p className="text-[10px] text-slate-400">
                  Swapped a physical card into your paper sleeve before tonight&rsquo;s pod? Resolve it cleanly for this game or save to your deck.
                </p>

                <select
                  value={offListScope}
                  onChange={(e) => setOffListScope(e.target.value as any)}
                  className="w-full px-2 py-1.5 rounded-lg bg-slate-950 border border-slate-700 text-white"
                >
                  <option value="this_game_only">This Game Only (auto-clears on rematch)</option>
                  <option value="save_deck_update">Permanent Paper Deck Update</option>
                </select>

                <input
                  value={offListQuery}
                  onChange={(e) => setOffListQuery(e.target.value)}
                  placeholder="Search Scryfall card name..."
                  className="w-full px-2.5 py-1.5 rounded-lg bg-slate-950 border border-slate-700 text-white"
                />

                <div className="space-y-1 max-h-44 overflow-y-auto">
                  {offListHits.map((card) => (
                    <div
                      key={card.scryfallId}
                      className="p-1.5 rounded-lg bg-slate-950 border border-slate-800 flex items-center justify-between gap-1.5"
                    >
                      <span className="truncate font-semibold text-slate-200">{card.name}</span>
                      <button
                        type="button"
                        onClick={() => {
                          if (!paper.enabled) {
                            onDispatch({ type: 'CONFIGURE_PAPER_MODE', enabled: true });
                          }
                          onDispatch({
                            type: 'ADD_PAPER_CARD_LIVE',
                            card,
                            scope: offListScope,
                            toZone: 'battlefield',
                          });
                          setStatusNotice(`Added ${card.name} (${offListScope.replace(/_/g, ' ')})!`);
                        }}
                        className="px-2 py-0.5 rounded bg-amber-500 text-slate-950 font-bold text-[10px] shrink-0"
                      >
                        + Play
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* 2. Pinned Video Markers */}
              <div className="p-3 rounded-xl bg-slate-900 border border-slate-800 space-y-2">
                <div className="font-bold text-cyan-300 flex items-center justify-between">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" />
                    <span>Pinned Video Markers</span>
                  </span>
                  {paper.videoMarkers.length > 0 && (
                    <button
                      type="button"
                      onClick={() => onDispatch({ type: 'CLEAR_VIDEO_MARKER', markerId: 'all' })}
                      className="text-[10px] text-rose-300 hover:text-rose-200 inline-flex items-center gap-0.5"
                    >
                      <Trash2 className="w-3 h-3" />
                      <span>Clear</span>
                    </button>
                  )}
                </div>

                <input
                  value={markerLabel}
                  onChange={(e) => setMarkerLabel(e.target.value)}
                  placeholder="Badge label (e.g. +1/+1, Stun, Tax)"
                  className="w-full px-2.5 py-1.5 rounded-lg bg-slate-950 border border-slate-700 text-white"
                />

                <div className="grid grid-cols-2 gap-1.5">
                  <select
                    value={markerKind}
                    onChange={(e) => setMarkerKind(e.target.value as any)}
                    className="px-2 py-1 rounded bg-slate-950 border border-slate-700 text-white"
                  >
                    <option value="counter">Counter</option>
                    <option value="tapped">Tapped</option>
                    <option value="damage">Damage</option>
                    <option value="note">Note</option>
                  </select>
                  <input
                    type="number"
                    min={0}
                    max={999}
                    value={markerCount}
                    onChange={(e) => setMarkerCount(Number(e.target.value) || 0)}
                    placeholder="Qty"
                    className="px-2 py-1 rounded bg-slate-950 border border-slate-700 text-white"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2 text-[10px] text-slate-400">
                  <label>
                    X ({markerX}%)
                    <input
                      type="range"
                      min={5}
                      max={95}
                      value={markerX}
                      onChange={(e) => setMarkerX(Number(e.target.value))}
                      className="w-full accent-cyan-400"
                    />
                  </label>
                  <label>
                    Y ({markerY}%)
                    <input
                      type="range"
                      min={5}
                      max={95}
                      value={markerY}
                      onChange={(e) => setMarkerY(Number(e.target.value))}
                      className="w-full accent-cyan-400"
                    />
                  </label>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    onDispatch({
                      type: 'SET_VIDEO_MARKER',
                      label: markerLabel,
                      kind: markerKind,
                      xPct: markerX,
                      yPct: markerY,
                      count: markerCount,
                    })
                  }
                  className="w-full py-1.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-extrabold"
                >
                  Pin Marker on Playmat
                </button>
              </div>

              {/* 3. Custom Alter / Proxy Hash Registration with Artist Credit */}
              <div className="p-3 rounded-xl bg-slate-900 border border-slate-800 space-y-2">
                <div className="font-bold text-violet-300 flex items-center gap-1">
                  <Palette className="w-3.5 h-3.5" />
                  <span>Register Custom Alter / Proxy</span>
                </div>
                <p className="text-[10px] text-slate-400">
                  Links an on-device 64-bit perceptual hash + artist credit to an official card in your deck (zero external image upload!).
                </p>

                <input
                  value={alterCardName}
                  onChange={(e) => setAlterCardName(e.target.value)}
                  placeholder="Deck card name (e.g. Sol Ring)"
                  className="w-full px-2.5 py-1.5 rounded-lg bg-slate-950 border border-slate-700 text-white"
                />

                <input
                  value={alterArtistCredit}
                  onChange={(e) => setAlterArtistCredit(e.target.value)}
                  placeholder="Mandatory Artist Credit (e.g. Klug Alters)"
                  className="w-full px-2.5 py-1.5 rounded-lg bg-slate-950 border border-slate-700 text-white"
                />

                <div className="flex gap-1">
                  <input
                    value={alterDHashHex}
                    onChange={(e) => setAlterDHashHex(e.target.value)}
                    placeholder="16-hex dHash..."
                    className="flex-1 px-2 py-1 rounded-lg bg-slate-950 border border-slate-700 font-mono text-[10px] text-white"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const snapped =
                        captureVideoDHash() ??
                        computeDeterministicCardArtHash(
                          alterCardName || 'alter',
                          alterArtistCredit || 'artist'
                        );
                      setAlterDHashHex(snapped);
                    }}
                    className="px-2 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-[10px] font-bold"
                  >
                    Snap Hash
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    onDispatch({
                      type: 'REGISTER_PAPER_ALTER',
                      cardName: alterCardName,
                      artistCredit: alterArtistCredit,
                      dHashHex: alterDHashHex,
                    });
                    setStatusNotice(`Submitted alter hash registration for ${alterCardName}.`);
                  }}
                  className="w-full py-1.5 rounded-lg bg-violet-600 hover:bg-violet-500 text-white font-extrabold"
                >
                  Register Alter Hash & Credit
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
